import { createContext, useContext, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR } from "../lib/format";
import api, { apiError } from "../lib/api";
import { useAuth } from "./AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const PrivacyContext = createContext(null);

export function PrivacyProvider({ children }) {
  const [hidden, setHidden] = useState(localStorage.getItem("rf_hidden") === "1");
  const [sensitiveUnlocked, setSensitiveUnlocked] = useState(false);
  const [pinDialog, setPinDialog] = useState(false);
  const [pin, setPin] = useState("");

  const setHiddenPersist = (v) => {
    localStorage.setItem("rf_hidden", v ? "1" : "0");
    setHidden(v);
  };

  const requestSensitive = () => setPinDialog(true);

  const verify = async () => {
    try {
      const { data } = await api.post("/auth/pin/verify", { pin });
      if (!data.valid) { toast.error("PIN incorrecto"); setPin(""); return; }
      setSensitiveUnlocked(true);
      setPinDialog(false);
      setPin("");
      toast.success("Datos sensibles visibles durante esta sesión");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PrivacyContext.Provider value={{ hidden, setHidden: setHiddenPersist, sensitiveUnlocked, requestSensitive }}>
      {children}
      <Dialog open={pinDialog} onOpenChange={setPinDialog}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle className="font-heading">Datos sensibles protegidos</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Introduce tu PIN secundario para ver capitales, indemnizaciones y beneficiarios.</p>
          <Input
            data-testid="sensitive-pin-input"
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="••••"
            className="text-center text-2xl tracking-[0.5em]"
            onKeyDown={(e) => e.key === "Enter" && pin.length === 4 && verify()}
          />
          <Button data-testid="sensitive-pin-submit" onClick={verify} disabled={pin.length !== 4} className="w-full">Desbloquear</Button>
        </DialogContent>
      </Dialog>
    </PrivacyContext.Provider>
  );
}

export const usePrivacy = () => useContext(PrivacyContext);

export function Money({ value, className = "", testId }) {
  const { hidden } = usePrivacy();
  return (
    <span className={className} data-testid={testId}>
      {hidden ? "•••••• €" : fmtEUR(value)}
    </span>
  );
}

export function Sensitive({ value, money = true, className = "", testId }) {
  const { hidden, sensitiveUnlocked, requestSensitive } = usePrivacy();
  const { user } = useAuth();
  if (user?.has_pin && !sensitiveUnlocked) {
    return (
      <button
        onClick={requestSensitive}
        data-testid={testId}
        title="Dato sensible: pulsa para desbloquear con PIN"
        className={`inline-flex items-center gap-1 text-muted-foreground hover:text-foreground ${className}`}
      >
        <Lock size={11} /> ••••••
      </button>
    );
  }
  const display = money ? (hidden ? "•••••• €" : fmtEUR(value)) : (hidden ? "••••••" : value);
  return <span className={className} data-testid={testId}>{display}</span>;
}
